import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSession, isAdmin, isPatient } from "@/lib/session";
import { Skeleton } from "@/components/ui/Feedback";

/**
 * Route guard replacing Flask's `login_required` redirects.
 *
 * The session query is resolved before anything renders, so a signed-in user
 * never sees a flash of the login screen on refresh, and a signed-out one
 * never sees a flash of protected data.
 */
export function RequireAuth({
  as,
  children,
  fallback,
}: {
  as: "admin" | "patient";
  children: ReactNode;
  /**
   * Rendered instead of redirecting when the visitor is signed out. Used by
   * public-facing routes such as /reservasi, where bouncing an anonymous
   * visitor straight to a login form reads as a dead end rather than an
   * invitation.
   */
  fallback?: ReactNode;
}) {
  const { data: session, isPending } = useSession();
  const location = useLocation();

  if (isPending) {
    return (
      <div className="min-h-dvh p-6" role="status" aria-label="Memeriksa sesi">
        <Skeleton className="mb-4 h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const authorised = as === "admin" ? isAdmin(session) : isPatient(session);
  if (!authorised) {
    if (fallback) return <>{fallback}</>;
    const loginPath = as === "admin" ? "/login" : "/patient/login";
    // `from` lets the login screen send the user back where they were headed.
    return <Navigate to={loginPath} replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}

/** Keeps an already-signed-in user out of the login screens. */
export function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const { data: session, isPending } = useSession();
  if (isPending) return null;
  if (isAdmin(session)) return <Navigate to="/admin" replace />;
  if (isPatient(session)) return <Navigate to="/patient/dashboard" replace />;
  return <>{children}</>;
}
